import React, { useCallback, useState } from "react";

import { Close as CloseIcon } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Drawer,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Snackbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";

import { ClipLoader } from "react-spinners";

import ChatPreviewContainer from "../Preview/ChatPreviewContainer";
import PreviewContainer from "../Preview/PreviewContainer";
import StepEditor from "../StepEditor";
import StepManager from "../StepManager";

const FunnelBuilder = ({
    onSave,
    form,
    selectedTemplatedID,
    isLandingPageFormLoading,
    templates,

    customSources,
    selectedCustomSourceId,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [steps, setSteps] = useState(form?.steps || []);
    const [selectedStepId, setSelectedStepId] = useState(null);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [editingStep, setEditingStep] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [activePanel, setActivePanel] = useState("steps"); // 'steps', 'editor', 'preview'
    const [funnelType, setFunnelType] = useState(form?.funnelType || "web"); // 'web' or 'chat'
    // Template fields from the provided data
    const templateFields = {
        title: form?.title || "Funnel Bot",
        theme: form?.theme || "light",
        logo: form?.logo || null,
        cta: form?.cta || "Continue",
        target_link: form?.target_link || "",
        code: form?.code || "",
        mmp: form?.mmp || "none",
        app_type: form?.app_type || [],
        cpp_android: form?.cpp_android || "",
        cpp_ios: form?.cpp_ios || "",
    };

    const generateStepId = useCallback(
        () => `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        [],
    );

    // Step management functions with error handling
    const handleAddStep = useCallback(
        (type) => {
            try {
                if (!type) {
                    throw new Error("Step type is required");
                }

                const newStep = {
                    id: generateStepId(),
                    title: "",
                    description: "",
                    type: type,
                    optional: false,
                    attributes: {},
                    order: steps.length,
                };

                const updatedSteps = [...steps, newStep];
                setSteps(updatedSteps);
                setSelectedStepId(newStep.id);
                setEditingStep(newStep);
                //onSave(updatedSteps);

                setSnackbar({
                    open: true,
                    message: `New ${type} step added`,
                    severity: "success",
                });

                // Close mobile drawer if open
                if (isMobile) {
                    setMobileDrawerOpen(false);
                    setActivePanel("editor");
                }
            } catch (error) {
                console.error("Error adding step:", error);
                setSnackbar({
                    open: true,
                    message: error.message || "Failed to add step",
                    severity: "error",
                });
            }
        },
        [steps, generateStepId, isMobile],
    );

    const handleEditStep = useCallback(
        (stepId) => {
            try {
                const step = steps.find((s) => s.id === stepId);
                if (step) {
                    setEditingStep(step);
                    setSelectedStepId(stepId);
                    if (isMobile) {
                        setActivePanel("editor");
                    }
                }
            } catch (error) {
                console.error("Error editing step:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to edit step",
                    severity: "error",
                });
            }
        },
        [steps, isMobile],
    );

    const handleSaveStep = useCallback(
        (updatedStep) => {
            try {
                if (!updatedStep || !updatedStep.id) {
                    throw new Error("Invalid step data");
                }

                const updatedSteps = steps.map((step) =>
                    step.id === updatedStep.id ? updatedStep : step,
                );
                setSteps(updatedSteps);
                setEditingStep(null);
                //onSave(updatedSteps);

                setSnackbar({
                    open: true,
                    message: "Step saved successfully",
                    severity: "success",
                    position: "top",
                });

                if (isMobile) {
                    setActivePanel("preview");
                }
            } catch (error) {
                console.error("Error saving step:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to save step",
                    severity: "error",
                });
            }
        },
        [steps, isMobile],
    );

    const handleDeleteStep = useCallback(
        (stepId) => {
            try {
                const updatedSteps = steps.filter((step) => step.id !== stepId);
                setSteps(updatedSteps);
                if (selectedStepId === stepId) {
                    setSelectedStepId(null);
                }
                if (editingStep?.id === stepId) {
                    setEditingStep(null);
                }
                //onSave(updatedSteps);

                setSnackbar({
                    open: true,
                    message: "Step deleted",
                    severity: "info",
                });
            } catch (error) {
                console.error("Error deleting step:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to delete step",
                    severity: "error",
                });
            }
        },
        [steps, selectedStepId, editingStep],
    );

    const handleDuplicateStep = useCallback(
        (stepId) => {
            try {
                const step = steps.find((s) => s.id === stepId);
                if (step) {
                    const duplicatedStep = {
                        ...step,
                        id: generateStepId(),
                        title: `${step.title} (Copy)`,
                        order: steps.length,
                    };
                    const updatedSteps = [...steps, duplicatedStep];
                    setSteps(updatedSteps);
                    //onSave(updatedSteps);

                    setSnackbar({
                        open: true,
                        message: "Step duplicated",
                        severity: "success",
                    });
                }
            } catch (error) {
                console.error("Error duplicating step:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to duplicate step",
                    severity: "error",
                });
            }
        },
        [steps, generateStepId],
    );

    const handleReorderSteps = useCallback((newSteps) => {
        try {
            // Update the order property for each step
            const reorderedSteps = newSteps.map((step, index) => ({
                ...step,
                order: index,
            }));

            setSteps(reorderedSteps);
            //onSave(reorderedSteps);

            setSnackbar({
                open: true,
                message: "Steps reordered successfully",
                severity: "success",
            });
        } catch (error) {
            console.error("Error reordering steps:", error);
            setSnackbar({
                open: true,
                message: "Failed to reorder steps",
                severity: "error",
            });
        }
    }, []);

    const handleSelectStep = useCallback(
        (stepId) => {
            setSelectedStepId(stepId);
            const step = steps.find((s) => s.id === stepId);
            if (step) {
                setEditingStep(step);
                if (isMobile) {
                    setActivePanel("editor");
                }
            }
        },
        [steps, isMobile],
    );

    const handleTogglePreview = useCallback(() => {
        setIsPreviewMode(!isPreviewMode);
        if (isMobile) {
            setActivePanel("preview");
        }
    }, [isPreviewMode, isMobile]);

    const handleCancelEdit = useCallback(() => {
        setEditingStep(null);
        if (isMobile) {
            setActivePanel("steps");
        }
    }, [isMobile]);

    const handleCloseSnackbar = useCallback(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    const handleMobileDrawerToggle = useCallback(() => {
        setMobileDrawerOpen(!mobileDrawerOpen);
    }, [mobileDrawerOpen]);

    // Mobile layout
    if (isMobile) {
        return (
            <Box
                sx={{
                    height: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                {/* Mobile Content */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {activePanel === "steps" && (
                        <Box
                            sx={{
                                flex: 1,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            {/* Funnel Type Selector - Mobile */}
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 1.5,
                                        fontWeight: 600,
                                        color: "text.primary",
                                    }}
                                >
                                    Funnel Type
                                </Typography>
                                <RadioGroup
                                    value={funnelType}
                                    onChange={(e) =>
                                        setFunnelType(e.target.value)
                                    }
                                    row
                                    sx={{ gap: 2, justifyContent: "center" }}
                                >
                                    <FormControlLabel
                                        value="web"
                                        checked={funnelType === "web"}
                                        control={
                                            <Radio
                                                size="small"
                                                sx={{
                                                    color: "text.secondary",
                                                    "&.Mui-checked": {
                                                        color: "warning.main",
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: "0.875rem",
                                                    fontWeight:
                                                        funnelType === "web"
                                                            ? 600
                                                            : 400,
                                                    color:
                                                        funnelType === "web"
                                                            ? "warning.main"
                                                            : "text.secondary",
                                                }}
                                            >
                                                Web Funnel
                                            </Typography>
                                        }
                                        sx={{
                                            m: 0,
                                            py: 0.75,
                                            px: 2,
                                            borderRadius: 1,
                                            bgcolor:
                                                funnelType === "web"
                                                    ? "warning.50"
                                                    : "transparent",
                                            border:
                                                funnelType === "web"
                                                    ? "1px solid"
                                                    : "1px solid transparent",
                                            borderColor:
                                                funnelType === "web"
                                                    ? "warning.main"
                                                    : "transparent",
                                            transition: "all 0.2s ease-in-out",
                                            "&:hover": {
                                                bgcolor:
                                                    funnelType === "web"
                                                        ? "warning.100"
                                                        : "action.hover",
                                            },
                                        }}
                                    />
                                    <FormControlLabel
                                        value="web-chat"
                                        checked={funnelType === "web-chat"}
                                        control={
                                            <Radio
                                                size="small"
                                                sx={{
                                                    color: "text.secondary",
                                                    "&.Mui-checked": {
                                                        color: "warning.main",
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: "0.875rem",
                                                    fontWeight:
                                                        funnelType ===
                                                        "web-chat"
                                                            ? 600
                                                            : 400,
                                                    color:
                                                        funnelType ===
                                                        "web-chat"
                                                            ? "warning.main"
                                                            : "text.secondary",
                                                }}
                                            >
                                                Chat Funnel
                                            </Typography>
                                        }
                                        sx={{
                                            m: 0,
                                            py: 0.75,
                                            px: 2,
                                            borderRadius: 1,
                                            bgcolor:
                                                funnelType === "web-chat"
                                                    ? "warning.50"
                                                    : "transparent",
                                            border:
                                                funnelType === "web-chat"
                                                    ? "1px solid"
                                                    : "1px solid transparent",
                                            borderColor:
                                                funnelType === "web-chat"
                                                    ? "warning.main"
                                                    : "transparent",
                                            transition: "all 0.2s ease-in-out",
                                            "&:hover": {
                                                bgcolor:
                                                    funnelType === "web-chat"
                                                        ? "warning.100"
                                                        : "action.hover",
                                            },
                                        }}
                                    />
                                </RadioGroup>
                            </Box>
                            <Box sx={{ flex: 1, overflow: "hidden" }}>
                                <StepManager
                                    steps={steps}
                                    onAddStep={handleAddStep}
                                    onEditStep={handleEditStep}
                                    onDeleteStep={handleDeleteStep}
                                    onDuplicateStep={handleDuplicateStep}
                                    onReorderSteps={handleReorderSteps}
                                    selectedStepId={selectedStepId}
                                    onSelectStep={handleSelectStep}
                                    isPreviewMode={isPreviewMode}
                                    onTogglePreview={handleTogglePreview}
                                />
                            </Box>
                        </Box>
                    )}

                    {activePanel === "editor" && editingStep && (
                        <Box sx={{ flex: 1, overflow: "hidden" }}>
                            <StepEditor
                                step={editingStep}
                                onSave={handleSaveStep}
                                steps={steps}
                                onCancel={handleCancelEdit}
                                templateFields={templateFields}
                                selectedCustomSourceId={selectedCustomSourceId}
                            />
                        </Box>
                    )}

                    {activePanel === "preview" && (
                        <Box sx={{ flex: 1, overflow: "hidden" }}>
                            {funnelType === "chat" && (
                                <ChatPreviewContainer
                                    steps={steps}
                                    templateFields={templateFields}
                                    selectedStepId={selectedStepId}
                                    isPreviewMode={isPreviewMode}
                                    onStepSelect={handleSelectStep}
                                />
                            )}
                            {funnelType === "web" && (
                                <PreviewContainer
                                    steps={steps}
                                    templateFields={templateFields}
                                    selectedStepId={selectedStepId}
                                    isPreviewMode={isPreviewMode}
                                    onStepSelect={handleSelectStep}
                                />
                            )}
                        </Box>
                    )}
                </Box>

                {/* Mobile Navigation */}
                <Box
                    sx={{
                        p: isSmallMobile ? 1 : 2,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        display: "flex",
                        flexDirection: "column",
                        gap: isSmallMobile ? 1 : 1.5,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-around",
                            gap: isSmallMobile ? 0.5 : 1,
                        }}
                    >
                        <Button
                            variant={
                                activePanel === "steps"
                                    ? "contained"
                                    : "outlined"
                            }
                            onClick={() => setActivePanel("steps")}
                            size={isSmallMobile ? "small" : "medium"}
                            sx={{
                                px: isSmallMobile ? 1 : 2,
                                width: "33.33%",
                            }}
                            style={{ minWidth: "unset !important" }}
                            color="primary"
                        >
                            Steps
                        </Button>
                        <Button
                            variant={
                                activePanel === "editor"
                                    ? "contained"
                                    : "outlined"
                            }
                            onClick={() => setActivePanel("editor")}
                            size={isSmallMobile ? "small" : "medium"}
                            disabled={!editingStep}
                            sx={{
                                px: isSmallMobile ? 1 : 2,
                                width: "33.33%",
                            }}
                            style={{ minWidth: "unset !important" }}
                            color="primary"
                        >
                            Edit
                        </Button>
                        <Button
                            variant={
                                activePanel === "preview"
                                    ? "contained"
                                    : "outlined"
                            }
                            onClick={() => setActivePanel("preview")}
                            size={isSmallMobile ? "small" : "medium"}
                            sx={{
                                px: isSmallMobile ? 1 : 2,
                                width: "33.33%",
                            }}
                            color="primary"
                        >
                            Preview
                        </Button>
                    </Box>

                    <Box
                        className="position-relative overflow-hidden z-1"
                        sx={{ width: "100%" }}
                    >
                        {isLandingPageFormLoading && (
                            <div className="circle-loader">
                                <ClipLoader color={"#000"} size={17} />
                            </div>
                        )}

                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() =>
                                onSave({
                                    ...form,
                                    steps,
                                    funnel_type: funnelType,
                                })
                            }
                            disabled={
                                steps.length === 0 ||
                                steps.some(
                                    (step) => !step.title || !step.attributes,
                                )
                            }
                            color="primary"
                            size={isSmallMobile ? "small" : "medium"}
                        >
                            Continue
                        </Button>
                    </Box>
                </Box>

                {/* Mobile Drawer */}
                <Drawer
                    anchor="left"
                    open={mobileDrawerOpen}
                    onClose={handleMobileDrawerToggle}
                    sx={{ "& .MuiDrawer-paper": { width: 280 } }}
                >
                    <Box sx={{ p: 2 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 2,
                            }}
                        >
                            <Typography variant="h6">Settings</Typography>
                            <IconButton onClick={handleMobileDrawerToggle}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Drawer>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: "100%" }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        );
    }

    // Desktop/Tablet layout
    return (
        <Box
            sx={{
                height: "100%",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            {/* Main Content */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    overflow: "hidden",
                    minHeight: 0, // Important for flex children
                }}
            >
                {/* Left Sidebar - Step Manager */}
                <Box
                    sx={{
                        width: isTablet ? 240 : 280,
                        minWidth: isTablet ? 240 : 280,
                        maxWidth: isTablet ? 240 : 280,
                        borderRight: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    <StepManager
                        steps={steps}
                        onAddStep={handleAddStep}
                        onEditStep={handleEditStep}
                        onDeleteStep={handleDeleteStep}
                        onDuplicateStep={handleDuplicateStep}
                        onReorderSteps={handleReorderSteps}
                        selectedStepId={selectedStepId}
                        onSelectStep={handleSelectStep}
                        isPreviewMode={isPreviewMode}
                        onTogglePreview={handleTogglePreview}
                    />
                </Box>

                {/* Main Content Area */}
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        minWidth: 0, // Important for flex children
                    }}
                >
                    {/* Right Panel - Split between Editor and Preview */}
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            overflow: "hidden",
                            minHeight: 0,
                        }}
                    >
                        {/* Step Editor */}
                        {editingStep && (
                            <Box
                                sx={{
                                    width: {
                                        xs: "100%",
                                        md: "30rem",
                                        lg: "60%",
                                        xl: "40rem",
                                    },
                                    borderRight: "1px solid",
                                    borderColor: "divider",
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                }}
                            >
                                <StepEditor
                                    step={editingStep}
                                    onSave={handleSaveStep}
                                    steps={steps}
                                    onCancel={handleCancelEdit}
                                    templateFields={templateFields}
                                    selectedCustomSourceId={
                                        selectedCustomSourceId
                                    }
                                />
                            </Box>
                        )}

                        {/* Preview Container */}
                        {
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                    minWidth: 0,
                                }}
                            >
                                {funnelType === "chat" && (
                                    <ChatPreviewContainer
                                        steps={steps}
                                        templateFields={templateFields}
                                        selectedStepId={selectedStepId}
                                        isPreviewMode={isPreviewMode}
                                        onStepSelect={handleSelectStep}
                                    />
                                )}
                                {funnelType === "web" && (
                                    <PreviewContainer
                                        steps={steps}
                                        templateFields={templateFields}
                                        selectedStepId={selectedStepId}
                                        isPreviewMode={isPreviewMode}
                                        onStepSelect={handleSelectStep}
                                    />
                                )}
                            </Box>
                        }
                    </Box>
                </Box>
            </Box>

            {/* Status Bar */}
            <Box
                sx={{
                    p: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {steps.length} steps configured
                    </Typography>
                </Box>

                {/* Funnel Type Selector - Center */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <RadioGroup
                            value={funnelType}
                            onChange={(e) => setFunnelType(e.target.value)}
                            row
                            sx={{ gap: 1 }}
                        >
                            <FormControlLabel
                                value="web"
                                control={
                                    <Radio
                                        size="small"
                                        sx={{
                                            color: "text.secondary",
                                            "&.Mui-checked": {
                                                color: "warning.main",
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Tooltip title="This type is web based funnel that will be displayed on a website.">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: "0.875rem",
                                                fontWeight:
                                                    funnelType === "web"
                                                        ? 600
                                                        : 400,
                                                color:
                                                    funnelType === "web"
                                                        ? "warning.main"
                                                        : "text.secondary",
                                            }}
                                        >
                                            Web
                                        </Typography>
                                    </Tooltip>
                                }
                                sx={{
                                    m: 0,
                                    py: 0.5,
                                    px: 1.5,
                                    borderRadius: 1,
                                    bgcolor:
                                        funnelType === "web"
                                            ? "warning.50"
                                            : "transparent",
                                    border:
                                        funnelType === "web"
                                            ? "1px solid"
                                            : "1px solid transparent",
                                    borderColor:
                                        funnelType === "web"
                                            ? "warning.main"
                                            : "transparent",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                        bgcolor:
                                            funnelType === "web"
                                                ? "warning.100"
                                                : "action.hover",
                                    },
                                }}
                            />
                            <FormControlLabel
                                value="chat"
                                control={
                                    <Radio
                                        size="small"
                                        sx={{
                                            color: "text.secondary",
                                            "&.Mui-checked": {
                                                color: "warning.main",
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Tooltip title="This type is chat based funnel that will be displayed on a chat platform.">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: "0.875rem",
                                                fontWeight:
                                                    funnelType === "chat"
                                                        ? 600
                                                        : 400,
                                                color:
                                                    funnelType === "chat"
                                                        ? "warning.main"
                                                        : "text.secondary",
                                            }}
                                        >
                                            Chat
                                        </Typography>
                                    </Tooltip>
                                }
                                sx={{
                                    m: 0,
                                    py: 0.5,
                                    px: 1.5,
                                    borderRadius: 1,
                                    bgcolor:
                                        funnelType === "chat"
                                            ? "warning.50"
                                            : "transparent",
                                    border:
                                        funnelType === "chat"
                                            ? "1px solid"
                                            : "1px solid transparent",
                                    borderColor:
                                        funnelType === "chat"
                                            ? "warning.main"
                                            : "transparent",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                        bgcolor:
                                            funnelType === "chat"
                                                ? "warning.100"
                                                : "action.hover",
                                    },
                                }}
                            />
                        </RadioGroup>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {steps.some((step) => !step.title || !step.attributes)
                            ? "Please fill in all step titles and attributes"
                            : "Continue to the next step"}
                    </Typography>
                    <div
                        className="position-relative overflow-hidden z-1"
                        style={{
                            flex: 1,
                        }}
                    >
                        {isLandingPageFormLoading && (
                            <div className="circle-loader">
                                <ClipLoader color={"#000"} size={17} />
                            </div>
                        )}

                        <Button
                            variant="contained"
                            onClick={() =>
                                onSave({
                                    ...form,
                                    steps,
                                    funnel_type: funnelType,
                                })
                            }
                            disabled={
                                steps.length === 0 ||
                                steps.some(
                                    (step) => !step.title || !step.attributes,
                                )
                            }
                            color="primary"
                        >
                            Continue
                        </Button>
                    </div>
                </Box>
            </Box>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FunnelBuilder;
